<template>
  <Transition name="fade">
    <div v-if="show" class="offline-full-page">
      <div class="offline-full-page__content">
        <!-- Заголовок -->
        <h1 class="offline-full-page__title">
          Нет интернета
        </h1>

        <!-- Описание -->
        <p class="offline-full-page__description">
          Проверьте сеть и попробуйте снова.
        </p>

        <!-- Телефон для заказа -->
        <p class="offline-full-page__phone-text">
          Или закажите по телефону
          <a
            :href="`tel:${phoneNumber}`"
            class="offline-full-page__phone-link"
          >
            {{ formattedPhone }}
          </a>
        </p>

        <!-- Кнопка повторить -->
        <button
          class="offline-full-page__retry-button"
          :disabled="isRetrying"
          @click="retry"
        >
          <span v-if="!isRetrying">Повторить</span>
          <span v-else class="offline-full-page__spinner-text">Проверяем подключение...</span>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { consola } from 'consola'
import { offlineManager, OfflineState } from '~/shared/lib/auth'
import { useInstitutionStore , getReadyInstitutionId } from '@entities/institution'
import { useUserProfileStore } from '@entities/user'
import { useCartStore } from '@entities/order'


interface Props {
  /**
   * Показывать ли полноэкранную страницу
   */
  modelValue?: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
}>()

const logger = consola.withTag('OfflineFullPage')

// Stores
const storeInstitution = useInstitutionStore()
const storeUserProfile = useUserProfileStore()
const cartStore = useCartStore()

// Состояние
const show = ref(props.modelValue || false)
const isRetrying = ref(false)
const isReconnecting = ref(false)
const savedPhone = ref<string | null>(null)

/**
 * Телефон для заказа
 * Приоритет: текущий из стора → сохраненный из storage → дефолтный
 */
const phoneNumber = computed(() => {
  return storeInstitution.currentCity?.phone || savedPhone.value
})

/**
 * Отформатированный телефон для отображения
 */
const formattedPhone = computed(() => {
  const phone = phoneNumber.value
  // Форматируем телефон: 88003020060 -> 8 800 302-00-60
  if (phone && phone.length >= 11) {
    const cleaned = phone.replaceAll(/\D/g, '')
    if (cleaned.length === 11 && cleaned.startsWith('8')) {
      return `${cleaned[0]} ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)}-${cleaned.slice(7, 9)}-${cleaned.slice(9)}`
    }
  }
  return phone
})

/**
 * Попробовать снова - повторная инициализация
 */
const retry = async () => {
  // Проверяем подключение
  if (!navigator.onLine) {
    logger.warn('[OfflineFullPage] Retry attempt but still offline')
    return
  }

  isRetrying.value = true

  try {
    logger.log('[OfflineFullPage] Retrying initialization...')

    // Повторно инициализируем данные
    const institutionID = await getReadyInstitutionId()
    await storeInstitution.fetchBranches(institutionID)

    if (storeInstitution.selectedBranch?.id) {
      await storeInstitution.fetchProducts(
        storeInstitution.selectedBranch.id,
        storeUserProfile.getUserId,
      )
    }

    await storeUserProfile.fetchUser()
    await cartStore.initializeCart()

    logger.log('[OfflineFullPage] ✅ Retry successful')

    // Скрываем страницу
    show.value = false
    emit('update:modelValue', false)
  } catch (error) {
    logger.error('[OfflineFullPage] ❌ Retry failed:', error)
    // Оставляем страницу видимой
  } finally {
    isRetrying.value = false
  }
}

/**
 * Обработка изменения состояния
 */
const handleStateChange = (state: OfflineState) => {
  isReconnecting.value = state === OfflineState.RECONNECTING

  if (state === OfflineState.ONLINE) {
    // Автоматически пробуем перезагрузить данные
    setTimeout(() => {
      retry()
    }, 500)
  }
}

/**
 * Обработка offline события
 */
const handleOffline = () => {
  show.value = true
  emit('update:modelValue', true)
}

/**
 * Обработка online события
 */
const handleOnline = () => {
  // Страница скроется автоматически после успешного retry
  isReconnecting.value = true
}

// Lifecycle hooks
onMounted(async () => {
  // ✅ Загружаем сохраненный номер телефона из storage
  try {
    const { useInstitutionPhoneStorage } = await import('@entities/institution')
    const phoneStorage = useInstitutionPhoneStorage()
    const phone = await phoneStorage.getInstitutionPhone()
    if (phone) {
      savedPhone.value = phone
      logger.log('[OfflineFullPage] Загружен номер из storage:', phone)
    }
  } catch (error) {
    logger.warn('[OfflineFullPage] Не удалось загрузить номер из storage:', error)
  }

  // Подписываемся на события
  offlineManager.on('stateChange', handleStateChange)
  offlineManager.on('offline', handleOffline)
  offlineManager.on('online', handleOnline)

  // Проверяем начальное состояние
  const currentState = offlineManager.getState()
  if (currentState === OfflineState.OFFLINE) {
    show.value = true
    emit('update:modelValue', true)
  } else if (currentState === OfflineState.RECONNECTING) {
    isReconnecting.value = true
  }

  logger.log('[OfflineFullPage] Mounted', {
    initialState: currentState,
    show: show.value,
    savedPhone: savedPhone.value,
  })
})

onBeforeUnmount(() => {
  logger.log('[OfflineFullPage] Unmounted')
})
</script>

<style lang="scss" scoped>
.offline-full-page {
  position: fixed;
  inset: 0;
  z-index: 9999;

  // Темный фон (почти черный)
  background-color: #000000;

  @apply flex items-center justify-center;

  // Safe area для iOS
  padding-top: env(safe-area-inset-top, 0);
  padding-bottom: env(safe-area-inset-bottom, 0);
  padding-left: env(safe-area-inset-left, 0);
  padding-right: env(safe-area-inset-right, 0);
}

.offline-full-page__content {
  @apply flex flex-col items-center text-center;
  @apply px-6;
  max-width: 100%;
  width: 100%;
}

.offline-full-page__title {
  @apply text-white font-bold mb-4;
  font-size: 28px;
  line-height: 1.2;
}

.offline-full-page__description {
  @apply text-gray-400 mb-6;
  font-size: 16px;
  line-height: 1.5;
}

.offline-full-page__phone-text {
  @apply text-gray-400 mb-8;
  font-size: 16px;
  line-height: 1.5;
}

.offline-full-page__phone-link {
  @apply text-orange-500;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    @apply text-orange-400;
  }

  &:active {
    @apply text-orange-600;
  }
}

.offline-full-page__retry-button {
  @apply bg-orange-500 text-white font-semibold rounded-lg;
  @apply transition-all duration-200;
  @apply active:bg-orange-600 active:scale-95;

  width: 100%;
  max-width: 280px;
  padding: 16px 24px;
  font-size: 16px;

  &:disabled {
    @apply opacity-70 cursor-not-allowed;
  }

  &:not(:disabled):hover {
    @apply bg-orange-600;
  }
}

.offline-full-page__spinner-text {
  @apply inline-flex items-center gap-2;

  &::before {
    content: '';
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spinner-rotate 0.8s linear infinite;
    display: inline-block;
  }
}

@keyframes spinner-rotate {
  100% {
    transform: rotate(360deg);
  }
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
